import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    ListItem,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import ReplyIcon from "@mui/icons-material/Reply";
import DeleteIcon from "@mui/icons-material/Delete";

import DefaultTable from "../../../components/Table/DefaultTable";
import { Input } from "../../../components/Input";
import { getQuoteAll, updateQuote, removeQuote } from "../../../apis/quote";
import { useCompanyData } from "../../../hook/useCompanyData";
import { Loader } from "../../../components/Loader";
import { CustomDialog } from "../../../components/modal/CustomDialog";
import { SelectComponent } from "../../../components/Select";
import { MenuItemData, ModelOptionDataType } from "../../../types/global.types";
import { useGlobalSnackBar } from "../../SnackbarContext";
import { Model, generateRandomId } from "../../../utils/model";
import { useModelData } from "../../../hook/useModelData";
import { setQuoteAdminModels } from "../../../store/actions/adminAuthActions";
import { usePermissionHook } from "../../../hook/usePermissionHook";
import { RootState } from "../../../store/store";
import { setQuoteState } from "../../../store/actions";

interface StatCardProps {
    value: number | string;
    description: string;
}

const menuItems: MenuItemData[] = [
    { value: "Pending", label: "Pending" },
    { value: "Sold", label: "Sold" },
    { value: "Expired", label: "Expired" },
];

const StatCard: React.FC<StatCardProps> = ({ value, description }) => (
    <Grid item sm={3} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
        <Card sx={{ width: "60%", minWidth: "180px", textAlign: "center" }}>
            <CardContent sx={{ pb: "10px !important" }}>
                <Typography variant="h5" component="div">
                    {value}
                </Typography>
                <Typography sx={{ mb: 1.5 }} color="text.secondary">
                    {description}
                </Typography>
            </CardContent>
        </Card>
    </Grid>
);

const headerData: string[] = [
    `${process.env.REACT_APP_DEALER_FULL_NAME}`,
    "Creator",
    "Contact",
    "Opt. Reference",
    "Quote #",
    "Date",
    "Status",
    "",
];

const sortableFields: any = {
    [`${process.env.REACT_APP_DEALER_FULL_NAME}`]: "dealer.dealer_name",
    Creator: "admin.email",
    Contact: "dealer.contact_name",
    "Opt. Reference": "quote.opt_reference",
    "Quote #": "quote.no",
    Date: "quote.created_at",
    Status: "quote.status",
};

export const Quotes: React.FC = () => {
    const history = useNavigate();
    const dispatch = useDispatch();

    const { company } = useCompanyData();

    const { showSnackBar } = useGlobalSnackBar();
    const { permissionReadOnly } = usePermissionHook();
    const { models, loading: modelsLoading } = useModelData();

    const [isLoading, setIsLoading] = useState<boolean | false>(true);
    const [quotes, setQuotes] = useState([]);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [editQuote, setEditQuote] = useState<any>();
    const [count, setCount] = useState<number>(0);
    const [lastMonthQuotesNumber, setLastMonthQuotesNumber] = useState<number>(0);
    const [yearQuotesNumber, setYearQuotesNumber] = useState<number>(0);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [searchTxt, setSearchTxt] = useState<string | undefined>();

    const quoteModels = useSelector(
        (state: RootState) => state.adminAuth.quoteAdminModels
    );

    const quoteState = useSelector(
        (state: RootState) => state.adminAuth.quoteState
    );

    const reQuoteHandle = (quote: any) => {
        if (permissionReadOnly) {
            permissionErrorMessage();
            return;
        }

        if (!models || models.length === 0) {
            showSnackBar("Models data is not loaded yet. Please try again shortly.", "error");
            return;
        }

        const modelOptionData = quote?.model_option_ids
            ? JSON.parse(quote?.model_option_ids)
            : [];

        const _modelOptionData = modelOptionData?.map(
            (res: ModelOptionDataType) => {
                let _model = models.find(
                    (model: Model) => model.ID === res.model_id.ID
                );

                return {
                    ..._model,
                    chose_options: res.option_ids,
                    qty: res.qty,
                    memo: quote?.memo,
                    optReference: quote?.opt_reference,
                    _id: generateRandomId(),
                };
            }
        );

        dispatch(setQuoteAdminModels(_modelOptionData));

        history("/quote_builder");
    };

    const removeQuoteHandle = async () => {
        if (permissionReadOnly) {
            permissionErrorMessage();
            return;
        }

        try {
            await removeQuote(editQuote.quoteId);
            showSnackBar("Removed the quote.");
            setOpenDeleteModal(false);
            fetchQuotes();
        } catch (err) {
            console.error(err);
        }
    };

    const fetchQuotes = async () => {
        try {
            const {
                data: quoteData,
                counts,
                lastMonthQuotesNumber,
                yearQuotesNumber,
            } = await getQuoteAll({
                page: quoteState.page + 1,
                pageSize: quoteState.pageSize,
                orderBy: quoteState.orderBy && sortableFields[quoteState.orderBy],
                order: quoteState.order,
                search: searchTxt,
            });

            const transformedData = quoteData?.map((quote: any, idx: number) => {
                const shouldTruncate = quote.opt_reference?.length > 50;
                const truncatedReference = shouldTruncate
                    ? `${quote.opt_reference.slice(0, 50)}...`
                    : quote.opt_reference;

                const optReferenceElement = shouldTruncate ? (
                    <Tooltip key={`optRef-${idx}`} title={quote.opt_reference}>
                        <span>{truncatedReference}</span>
                    </Tooltip>
                ) : (
                    <span>{truncatedReference}</span>
                );
                return {
                    // activity: quote.activity,
                    dealer:
                        quote.dealer_id === "ADMIN"
                            ? company?.company_name
                            : quote?.dealer_name,
                    creator:
                        quote.dealer_id === "ADMIN"
                            ? quote?.adminCreator || "N/A"
                            : quote?.dealerCreator,
                    contact:
                        quote.dealer_id === "ADMIN"
                            ? `${quote?.first_name || ''} ${quote?.last_name || ''}`
                            : quote?.contact_name,
                    opt_reference: optReferenceElement,
                    quotes: (
                        <ListItem
                            disablePadding
                            component={Link}
                            to={`/quote/${quote.quoteId}`}
                        >
                            {quote.no}
                        </ListItem>
                    ),
                    last_activity: moment(quote.createdAt).format("MM/DD/YYYY"),
                    status: quote?.quote_status,
                    edit: (
                        <div>
                            <Tooltip title="Edit">
                                <IconButton
                                    color="primary"
                                    onClick={() => {
                                        setOpenModal(true);
                                        setEditQuote(quote);
                                    }}
                                >
                                    <ModeEditIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Re-quote">
                                <IconButton
                                    color="primary"
                                    onClick={() => reQuoteHandle(quote)}
                                    disabled={quote.dealer_id !== "ADMIN"}
                                >
                                    <ReplyIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove">
                                <IconButton
                                    color="error"
                                    onClick={() => {
                                        setOpenDeleteModal(true);
                                        setEditQuote(quote);
                                    }}
                                // disabled={quote.dealer_id !== "ADMIN"}setOpenDeleteModal
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </div>
                    ),
                }
            });

            setQuotes(transformedData);

            setLastMonthQuotesNumber(lastMonthQuotesNumber);
            setYearQuotesNumber(yearQuotesNumber);
            setCount(counts);
            setIsLoading(false);
        } catch (error) {
            console.error("Failed to fetch admins", error);
        }
    };

    useEffect(() => {
        if (!company || modelsLoading) return;
        setIsLoading(true);

        fetchQuotes();
    }, [company, modelsLoading, quoteState]);

    useEffect(() => {
        if (searchTxt === "") {
            fetchQuotes();
        }
    }, [searchTxt]);

    const permissionErrorMessage = () => {
        showSnackBar("Invalid operation: Only Administrators and Sales can make this change.", "error");
    };

    const handleChangeQuoteStatus = async () => {
        if (permissionReadOnly) {
            permissionErrorMessage();
            return;
        }

        try {
            await updateQuote({
                ID: editQuote.quoteId,
                status: editQuote.quote_status,
            });
            showSnackBar("Changed this quote status.");
            setOpenModal(false);
            fetchQuotes();
        } catch (err) {
            console.error(err);
        }
    };

    const handleChangePage = (page: number) => {
        dispatch(setQuoteState({ page }));
    };

    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setQuoteState({ page: 0, pageSize: Number(e.target.value) }));
    };

    const handleQuoteBuild = () => {
        if (permissionReadOnly) {
            permissionErrorMessage();
            return;
        }

        if (quoteModels && quoteModels.length) {
            history("/quote_builder");
            return;
        }

        history("/models_list");
    };

    const handleSort = (column: string) => {
        if (!sortableFields[column]) return;
        const isAsc = quoteState.orderBy === column && quoteState.order === "asc";
        dispatch(setQuoteState({ order: isAsc ? "desc" : "asc", orderBy: column }));
    };

    if (isLoading) return <Loader isLoading={isLoading} />;

    return (
        <>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                    <StatCard
                        value={lastMonthQuotesNumber}
                        description="Quotes (Last 30 Days)"
                    />
                    <StatCard value={yearQuotesNumber} description="Quotes (This Year)" />
                    <StatCard value={count} description="Quotes (All time)" />
                    <Grid item sm={3} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                        <Card
                            style={{ textAlign: "center", cursor: "pointer" }}
                            onClick={() => handleQuoteBuild()}
                            sx={{ width: "60%", minWidth: "180px", textAlign: "center" }}
                        >
                            <CardContent sx={{ pb: "10px !important" }}>
                                <Typography variant="h5" component="div">
                                    +
                                </Typography>
                                <Typography sx={{ mb: 1.5 }} color="text.secondary">
                                    Build Quote
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ flexGrow: 1, mt: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Input
                            label="Search:"
                            onChange={(e) => setSearchTxt(e.target.value)}
                            value={searchTxt}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Stack spacing={2} direction="row">
                            <Button
                                variant="contained"
                                onClick={() => {
                                    if (!searchTxt) {
                                        showSnackBar("You need to enter the text to search first.", "error");
                                    } else if (!quoteState.orderBy) {
                                        showSnackBar(
                                            "You need to select a column to search first.", "error"
                                        );
                                    } else {
                                        fetchQuotes();
                                    }
                                }}
                            >
                                Apply
                            </Button>
                            <Button variant="outlined" onClick={() => setSearchTxt("")}>
                                Reset
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
                <h3 style={{ textAlign: "center" }}>Recent Activity</h3>
                <DefaultTable
                    headerData={headerData}
                    rowsData={quotes}
                    rowsPerPage={quoteState.pageSize}
                    page={quoteState.page}
                    count={count}
                    handleChangePage={(
                        e: React.ChangeEvent<HTMLInputElement>,
                        page: number
                    ) => handleChangePage(page)}
                    handleChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeRowsPerPage(e)
                    }
                    handleSort={handleSort}
                    order={quoteState.order}
                    orderBy={quoteState.orderBy}
                    sortableFields={sortableFields}
                />
            </Box>

            {editQuote && (
                <CustomDialog
                    openModal={openModal}
                    handleClose={() => setOpenModal(false)}
                    handleAction={handleChangeQuoteStatus}
                    buttonTitle="save"
                    width="xs"
                    title="Change"
                >
                    <p />
                    <SelectComponent
                        menuItems={menuItems}
                        // width="180px"
                        label="Status"
                        value={editQuote?.quote_status}
                        onChange={(e) =>
                            setEditQuote({ ...editQuote, quote_status: e.target.value })
                        }
                    />
                </CustomDialog>
            )}

            <CustomDialog
                title="Are you sure you want to remove this quote?"
                openModal={openDeleteModal}
                handleClose={() => setOpenDeleteModal(false)}
                handleAction={removeQuoteHandle}
                buttonTitle={"Yes"}
                children={undefined}
            />
        </>
    );
};
